import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRightIcon } from 'lucide-react';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { AtnaAssistant } from '../components/atna/AtnaAssistant';
import { ATNA_CONFIG } from '../components/atna/atnaConfig';
import { useLanguage } from '../components/LanguageContext';
import { trackEvent as track } from '../utils/analytics';
const reveal = {
  initial: {
    opacity: 0,
    y: 30
  },
  whileInView: {
    opacity: 1,
    y: 0
  },
  viewport: {
    once: true,
    margin: '-80px'
  },
  transition: {
    duration: 0.7
  }
};
export function Demo() {
  const {
    lang
  } = useLanguage();
  const isFR = lang === 'FR';
  useEffect(() => {
    track('Page Viewed', {
      page: 'Demo'
    });
  }, []);
  const modules = [{
    to: '/requests',
    fr: 'Requêtes IA (311)',
    en: 'AI Requests (311)'
  }, {
    to: '/collect',
    fr: 'Collecte IA',
    en: 'AI Collect'
  }, {
    to: '/communications',
    fr: 'Communications IA',
    en: 'AI Communications'
  }, {
    to: '/smart-water',
    fr: 'Compteurs intelligents',
    en: 'AI Smart Water'
  }, {
    to: '/digital-card',
    fr: 'Carte numérique',
    en: 'Digital Card'
  }, {
    to: '/analytics',
    fr: 'Analytique',
    en: 'Analytics'
  }];
  return <div className="w-full min-h-screen bg-white" data-id="element-2049">
      <Header data-id="element-2050" />
      <main data-id="element-2051">
        {/* Hero */}
        <section className="bg-white pt-40 lg:pt-48 pb-16 px-6" data-id="element-2052">
          <div className="max-w-4xl mx-auto text-center" data-id="element-2053">
            <motion.div {...reveal} className="font-sans text-xs font-semibold tracking-[0.2em] text-[#4F46E5] uppercase mb-8" data-id="element-2054">
              {isFR ? 'Démonstration' : 'See it live'}
            </motion.div>
            <motion.h1 {...reveal} transition={{
            duration: 0.7,
            delay: 0.05
          }} className="font-serif text-4xl md:text-5xl lg:text-[3.5rem] text-[#121212] leading-[1.1] tracking-tight mb-6" data-id="element-2055">
              {isFR ? 'Voir BCITI en action' : 'See BCITI in action'}
            </motion.h1>
            <motion.p {...reveal} transition={{
            duration: 0.7,
            delay: 0.1
          }} className="font-sans text-lg md:text-xl text-[#121212]/60 leading-relaxed max-w-2xl mx-auto mb-10" data-id="element-2056">
              {isFR ? "Essayez l'assistant ATNA, parcourez les résultats en production et explorez chaque module — en un seul endroit." : 'Try the ATNA assistant, review production results and explore each module — all in one place.'}
            </motion.p>
            <motion.div {...reveal} transition={{
            duration: 0.7,
            delay: 0.15
          }} data-id="element-2057">
              <Link to="/request-demo" className="inline-flex items-center gap-2 px-8 py-4 bg-[#4F46E5] text-white text-sm font-medium uppercase tracking-wider hover:bg-[#4F46E5]/90 transition-colors" data-id="element-2058">
                {isFR ? 'Demander une démo' : 'Request a Demo'}
                <ArrowRightIcon className="w-4 h-4" data-id="element-2059" />
              </Link>
            </motion.div>
          </div>
        </section>

        {/* Live ATNA assistant */}
        <section className="py-20 px-6 border-t border-[#E4E6EC] bg-[#FAFAFB]" data-id="element-2060">
          <div className="max-w-3xl mx-auto" data-id="element-2061">
            <div className="text-center mb-10" data-id="element-2062">
              <div className="font-sans text-xs font-semibold tracking-[0.2em] text-[#4F46E5] uppercase mb-4" data-id="element-2063">
                {isFR ? 'Assistant ATNA' : 'ATNA assistant'}
              </div>
              <h2 className="font-serif text-3xl md:text-4xl text-[#121212]" data-id="element-2064">
                {isFR ? 'Posez une question. En direct.' : 'Ask a question. Live.'}
              </h2>
            </div>
            <AtnaAssistant vertical={ATNA_CONFIG['home']} page="/demo" data-id="element-2065" />
          </div>
        </section>

        {/* Production results */}
        <section className="py-24 px-6 border-t border-[#E4E6EC]" data-id="element-2066">
          <div className="max-w-4xl mx-auto" data-id="element-2067">
            <div className="text-center font-sans text-xs font-semibold tracking-[0.2em] uppercase text-[#4F46E5] mb-10" data-id="element-2068">
              {isFR ? 'Résultats en production' : 'Production results'}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 border border-[#E4E6EC] divide-y md:divide-y-0 md:divide-x divide-[#E4E6EC]" data-id="element-2069">
              <div className="p-8 md:p-12 text-center" data-id="element-2070">
                <div className="font-serif text-5xl md:text-6xl text-[#121212] leading-none mb-5" data-id="element-2071">
                  400 <span className="text-[#4F46E5]" data-id="element-2072">→</span> 5
                </div>
                <div className="font-sans text-sm text-[#121212]/60 leading-relaxed max-w-xs mx-auto" data-id="element-2073">
                  {isFR ? 'Personnes en attente au comptoir pour un renouvellement de carte numérique' : 'People waiting in line for a digital card renewal'}
                </div>
              </div>
              <div className="p-8 md:p-12 text-center" data-id="element-2074">
                <div className="font-serif text-5xl md:text-6xl text-[#121212] leading-none mb-5" data-id="element-2075">
                  {isFR ? '130\u202f000' : '130,000'}
                  <span className="text-[#4F46E5]" data-id="element-2076">+</span>
                </div>
                <div className="font-sans text-sm text-[#121212]/60 leading-relaxed max-w-xs mx-auto" data-id="element-2077">
                  {isFR ? 'Citoyens joignables en moins de 30 minutes lors d’une urgence' : 'Residents reachable in under 30 minutes during an emergency'}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Explore modules */}
        <section className="py-24 px-6 border-t border-[#E4E6EC] bg-[#FAFAFB]" data-id="element-2078">
          <div className="max-w-5xl mx-auto" data-id="element-2079">
            <div className="text-center mb-12" data-id="element-2080">
              <div className="font-sans text-xs font-semibold tracking-[0.2em] text-[#4F46E5] uppercase mb-4" data-id="element-2081">
                {isFR ? 'Modules' : 'Modules'}
              </div>
              <h2 className="font-serif text-3xl md:text-4xl text-[#121212]" data-id="element-2082">
                {isFR ? 'Explorez chaque module' : 'Explore each module'}
              </h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-2083">
              {modules.map(m => <Link key={m.to} to={m.to} className="border border-[#E4E6EC] bg-white p-6 flex items-center justify-between hover:border-[#4F46E5]/30 transition-colors" data-id="element-2084">
                  <span className="font-serif text-lg text-[#121212]" data-id="element-2085">
                    {isFR ? m.fr : m.en}
                  </span>
                  <ArrowRightIcon className="w-4 h-4 text-[#4F46E5]" data-id="element-2086" />
                </Link>)}
            </div>
          </div>
        </section>

        {/* Case study */}
        <section className="py-24 px-6 border-t border-[#E4E6EC]" data-id="element-2087">
          <div className="max-w-3xl mx-auto text-center" data-id="element-2088">
            <div className="font-sans text-xs font-semibold tracking-[0.2em] text-[#4F46E5] uppercase mb-4" data-id="element-2089">
              {isFR ? 'Étude de cas' : 'Case study'}
            </div>
            <h2 className="font-serif text-3xl md:text-4xl text-[#121212] mb-6" data-id="element-2090">
              {isFR ? 'Brossard, en production depuis 2017' : 'Brossard, live since 2017'}
            </h2>
            <Link to="/brossard" className="inline-flex items-center gap-2 font-sans text-base font-medium text-[#4F46E5] hover:text-[#4F46E5]/80 transition-colors" data-id="element-2091">
              {isFR ? "Explorer l'étude de cas" : 'Explore the case study'}
              <ArrowRightIcon className="w-4 h-4" data-id="element-2092" />
            </Link>
          </div>
        </section>

        {/* Final CTA */}
        <section className="py-32 px-6 border-t border-[#E4E6EC] bg-[#FAFAFB]" data-id="element-2093">
          <div className="max-w-3xl mx-auto text-center" data-id="element-2094">
            <h2 className="font-serif text-3xl md:text-4xl text-[#121212] mb-8" data-id="element-2095">
              {isFR ? 'Prêt à voir BCITI sur vos données ?' : 'Ready to see BCITI on your data?'}
            </h2>
            <Link to="/request-demo" className="inline-flex items-center gap-2 px-8 py-4 bg-[#4F46E5] text-white text-sm font-medium uppercase tracking-wider hover:bg-[#4F46E5]/90 transition-colors" data-id="element-2096">
              {isFR ? 'Demander une démo' : 'Request a Demo'}
              <ArrowRightIcon className="w-4 h-4" data-id="element-2097" />
            </Link>
          </div>
        </section>
      </main>
      <Footer data-id="element-2098" />
    </div>;
}